<!-- ToastProvider - Provides toast context via provide/inject -->
<script setup lang="ts">
import { ref, provide, type InjectionKey } from 'vue'
import Toast from './Toast.vue'
import type { ToastMessage } from '@/types'

interface ToastContext {
  toasts: ToastMessage[]
  showToast: (type: ToastMessage['type'], title: string, message?: string, duration?: number) => string
  removeToast: (id: string) => void
  clearToasts: () => void
}

const ToastInjectionKey: InjectionKey<ToastContext> = Symbol('toast')

const toasts = ref<ToastMessage[]>([])

const showToast = (
  type: ToastMessage['type'],
  title: string,
  message?: string,
  duration = 4000
): string => {
  const id = `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`
  const toast: ToastMessage = { id, type, title, message, duration }
  toasts.value.push(toast)

  if (duration > 0) {
    setTimeout(() => removeToast(id), duration)
  }

  return id
}

const removeToast = (id: string) => {
  const index = toasts.value.findIndex(t => t.id === id)
  if (index !== -1) {
    toasts.value.splice(index, 1)
  }
}

const clearToasts = () => {
  toasts.value = []
}

provide(ToastInjectionKey, {
  toasts,
  showToast,
  removeToast,
  clearToasts,
})

defineExpose({ toasts, showToast, removeToast, clearToasts })
</script>

<template>
  <div class="toast-container" aria-live="polite" aria-label="Notificaciones">
    <Toast
      v-for="toast in toasts"
      :key="toast.id"
      :id="toast.id"
      :type="toast.type"
      :title="toast.title"
      :message="toast.message"
      :on-close="removeToast"
    />
  </div>
</template>

<style scoped>
.toast-container {
  position: fixed;
  top: calc(var(--header-height) + var(--space-md));
  right: var(--space-md);
  z-index: var(--z-tooltip);
  display: flex;
  flex-direction: column;
  gap: var(--space-sm);
  max-width: 400px;
  pointer-events: none;
}

@media (max-width: 480px) {
  .toast-container {
    left: var(--space-md);
    right: var(--space-md);
    max-width: none;
  }
}
</style>